import { useEffect, useMemo } from 'react'
import { useSnackbar } from 'notistack'
import { DocumentNode, useLazyQuery } from '@apollo/client'

import { PaginationSource, usePagination } from 'shared/hooks'
import { getPageOnBySource } from 'shared/hooks/use-pagination'
import {
  AddActionHandler,
  AddActionOption,
  useSingleSelectDialogController,
} from '../../SingleSelectTable'
import {
  AdminCandidate,
  AdminCandidatesQueryData,
  AdminsScopeVariables,
} from '../types'

interface UseAddAdminDialogArgs<TCandidate extends AdminCandidate> {
  open: boolean
  candidatesQuery: DocumentNode
  scopeVariables: AdminsScopeVariables
  isCandidateAdmin: (candidate: TCandidate) => boolean | null | undefined
  paginationSource: PaginationSource
  addAdminSuccessText: string
  onSubmit: AddActionHandler
}

export const useAddAdminDialog = <TCandidate extends AdminCandidate>({
  open,
  candidatesQuery,
  scopeVariables,
  isCandidateAdmin,
  paginationSource,
  addAdminSuccessText,
  onSubmit,
}: UseAddAdminDialogArgs<TCandidate>) => {
  const initPageSize = getPageOnBySource(paginationSource) ?? 20
  const { enqueueSnackbar } = useSnackbar()

  const [
    fetchCandidates,
    { data, loading: dataLoading, refetch: refetchCandidates },
  ] = useLazyQuery<AdminCandidatesQueryData<TCandidate>>(candidatesQuery, {
    variables: { ...scopeVariables, first: initPageSize },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const candidates = data?.viewer?.users
  const pageInfo = candidates?.pageInfo

  useEffect(() => {
    if (open) {
      fetchCandidates()
    }
  }, [fetchCandidates, open])

  const normalizedPageInfo = useMemo(
    () =>
      pageInfo
        ? {
            startCursor: pageInfo.startCursor ?? '',
            endCursor: pageInfo.endCursor ?? '',
            hasNextPage: Boolean(pageInfo.hasNextPage),
            hasPreviousPage: Boolean(pageInfo.hasPreviousPage),
          }
        : undefined,
    [pageInfo]
  )

  const {
    pagination,
    search: { query, setQuery },
  } = usePagination({
    initPageSize,
    pageInfo: normalizedPageInfo,
    loading: dataLoading,
    totalCount: candidates?.totalCount ?? 0,
    source: paginationSource,
    refetch: variables =>
      refetchCandidates({
        ...scopeVariables,
        query: variables.query || undefined,
        first: variables.first,
        last: variables.last,
        after: variables.after,
        before: variables.before,
      }),
  })

  const onSuccess = () => {
    enqueueSnackbar(addAdminSuccessText, { variant: 'success' })
    refetchCandidates()
  }

  const options = useMemo<AddActionOption[]>(
    () =>
      candidates?.nodes.map(candidate => ({
        value: candidate.id,
        label: candidate.email || '-',
        disabled: isCandidateAdmin(candidate) === true,
      })) ?? [],
    [candidates?.nodes, isCandidateAdmin]
  )

  return useSingleSelectDialogController({
    query,
    setQuery,
    dataLoading,
    options,
    pagination,
    onSubmit,
    onSuccess,
  })
}
